import { useEffect, useState, useRef } from 'react';

interface UseAnchorTooltipOptions {
  /** data-lesson-anchor 屬性值 */
  anchor: string;
  /** 是否啟用（false 時立即清除並停止 polling） */
  enabled: boolean;
  /** 從 DOMRect 計算 tooltip 的 CSSProperties */
  calcStyle: (rect: DOMRect) => React.CSSProperties;
  /** 找到 anchor 後是否自動 scrollIntoView（僅執行一次） */
  scrollIntoView?: boolean;
  /** 首次 poll 的延遲 ms（預設 100） */
  initialDelay?: number;
}

/**
 * Polling-based hook：持續追蹤 data-lesson-anchor 元素的位置，
 * 回傳用於 tooltip 定位的 CSSProperties。
 *
 * - anchor 出現時計算位置，消失時回傳 null
 * - 內建 change-detection：位置不變時不觸發 re-render
 */
export function useAnchorTooltip({
  anchor,
  enabled,
  calcStyle,
  scrollIntoView = false,
  initialDelay = 100,
}: UseAnchorTooltipOptions): React.CSSProperties | null {
  const [style, setStyle] = useState<React.CSSProperties | null>(null);
  const lastKeyRef = useRef('');
  const scrolledRef = useRef(false);
  // 用 ref 持有 calcStyle 避免 polling effect 因 calcStyle 參考變化而重啟
  const calcStyleRef = useRef(calcStyle);
  calcStyleRef.current = calcStyle;

  // enabled 或 anchor 改變時重置 scroll 追蹤
  useEffect(() => {
    scrolledRef.current = false;
  }, [enabled, anchor]);

  useEffect(() => {
    if (!enabled) {
      setStyle(null);
      lastKeyRef.current = '';
      return;
    }

    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = () => {
      if (stopped) return;
      const el = document.querySelector(`[data-lesson-anchor="${anchor}"]`);
      if (el) {
        const rect = el.getBoundingClientRect();
        const key = `${rect.top},${rect.left},${rect.bottom},${rect.right}`;
        if (key !== lastKeyRef.current) {
          lastKeyRef.current = key;
          setStyle(calcStyleRef.current(rect));
        }
        if (scrollIntoView && !scrolledRef.current) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          scrolledRef.current = true;
        }
      } else {
        if (lastKeyRef.current !== '') {
          lastKeyRef.current = '';
          setStyle(null);
        }
      }
      timer = setTimeout(poll, 300);
    };

    timer = setTimeout(poll, initialDelay);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, anchor, scrollIntoView, initialDelay]);

  return style;
}
